<template>
    <div v-if="loading == false">
        <ChirpListClient
            :data_source="rows"
            :export_api="export_api"
            :createAction="false"
            :createButton="false"
            :hideFilter="true"
            :loadingColumns="loadingTable"
            :column_meta="columnMeta"
            :totalCount="totalCount"
            :storeKey="storeKey"
            :headerText="'Pre-Members'"
        >
            <template control="">
                <div class="pre-members-status-control">
                    <label class="status-control-label">Status</label>
                    <select v-model="conversionStatus" @change="onStatusChange" class="status-control-select">
                        <option value="">All</option>
                        <option value="converted">Converted</option>
                        <option value="pending">Pending</option>
                        <option value="conflict">Conflict</option>
                    </select>
                </div>
            </template>
        </ChirpListClient>
    </div>
    <div v-else>Loading...</div>
</template>

<script>
    import ChirpListClient from '@/components/general/list/ChirpListClient.vue';
    import { tryGetFilter } from '@/util/tryGetFilter';
    import { preMembers } from '@/util/apiRequests';
    import axios from 'axios';

    const STATUS_LABELS = {
        converted: 'Converted',
        pending: 'Pending',
        conflict: 'Conflict',
    };

    export default {
        name: 'PreMembersList',
        components: { ChirpListClient },
        data() {
            return {
                rows: [],
                loading: true,
                loadingTable: false,
                totalCount: 0,
                conversionStatus: '',
                export_api: preMembers.list(),
                columnMeta: [
                    { field: 'first_name', headerText: 'First Name' },
                    { field: 'last_name', headerText: 'Last Name' },
                    { field: 'email', headerText: 'Email', width: 220 },
                    { field: 'phone', headerText: 'Phone' },
                    { field: 'date_of_birth', headerText: 'DOB' },
                    { field: 'created_at', headerText: 'Added' },
                    { field: 'conversion_status', headerText: 'Status' },
                ],
                cancelTokenSource: null,
            };
        },
        computed: {
            criteria() {
                return tryGetFilter(this.$store, this.storeKey);
            },
            storeKey() {
                return 'pre-members';
            },
        },
        methods: {
            async load_data() {
                try {
                    if (this.cancelTokenSource) {
                        this.cancelTokenSource.cancel('Previous request canceled');
                    }
                    this.cancelTokenSource = axios.CancelToken.source();

                    const params = this.criteriaToParams();
                    const res = await this.$api.get(this.export_api, {
                        params,
                        cancelToken: this.cancelTokenSource.token,
                    });
                    if (!res) return;

                    if (res.status >= 400) {
                        this.$toasted.error('Failed to fetch pre-members.');
                        return;
                    }

                    const payload = res.data?.data ?? {};
                    const sourceRows = payload.rows ?? [];

                    this.rows = sourceRows.map((row) => ({
                        id: row.id,
                        first_name: row.first_name,
                        last_name: row.last_name,
                        email: row.email,
                        phone: row.phone,
                        date_of_birth: this.formatDate(row.date_of_birth),
                        created_at: this.formatDate(row.created_at),
                        conversion_status: STATUS_LABELS[row.conversion_status] || 'Pending',
                    }));
                    this.totalCount = payload.total_count ?? 0;
                } catch (err) {
                    if (axios.isCancel(err)) return;
                    this.$toasted.error('Failed to fetch pre-members.');
                }

                this.$nextTick(() => {
                    this.loading = false;
                });
            },
            criteriaToParams() {
                const params = {
                    page: this.criteria.page?.page_num || 1,
                    limit: this.criteria.page?.num_per_page || 10,
                };

                const sortKeys = Object.keys(this.criteria.sort || {});
                if (sortKeys.length) {
                    const key = sortKeys[0];
                    const dir = String(this.criteria.sort[key]).toLowerCase().startsWith('desc')
                        ? 'desc'
                        : 'asc';
                    params.sort_field = key;
                    params.sort_dir = dir;
                } else {
                    params.sort_field = 'created_at';
                    params.sort_dir = 'desc';
                }

                if (this.criteria.search?.like_all) {
                    params.search = this.criteria.search.like_all;
                }
                if (this.conversionStatus) {
                    params.conversion_status = this.conversionStatus;
                }
                return params;
            },
            formatDate(value) {
                if (!value) return '';
                const s = String(value);
                if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
                    const [y, m, d] = s.substring(0, 10).split('-');
                    return `${m}/${d}/${y.substring(2)}`;
                }
                const dt = new Date(s);
                if (isNaN(dt.getTime())) return '';
                const m = String(dt.getMonth() + 1).padStart(2, '0');
                const d = String(dt.getDate()).padStart(2, '0');
                const y = String(dt.getFullYear()).substring(2);
                return `${m}/${d}/${y}`;
            },
            onStatusChange() {
                const newCriteria = {
                    ...this.criteria,
                    page: { ...(this.criteria.page || {}), page_num: 1 },
                };
                this.$store.commit('filters/updateFilter', {
                    stateKey: this.storeKey,
                    criteria: newCriteria,
                });
            },
        },
        watch: {
            criteria: {
                deep: true,
                async handler() {
                    await this.load_data();
                },
            },
        },
        async created() {
            await this.load_data();
        },
    };
</script>

<style scoped>
    .pre-members-status-control {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        margin-left: 10px;
    }

    .status-control-label {
        font-size: 0.9rem;
        color: #2e3343;
        margin: 0;
        font-weight: 500;
    }

    .status-control-select {
        border: 1px solid #d9dde5;
        background: white;
        border-radius: 5px;
        padding: 6px 10px;
        font-size: 0.95rem;
        color: #2e3343;
        box-shadow: 0px 1px 6px rgba(58, 64, 82, 0.15);
    }

    .status-control-select:focus {
        outline: none;
        border-color: var(--button-color, #5057c3);
    }
</style>
